import { AxiosInstance } from 'axios';
import { CollectionResource } from '@app/data/resources';
import {
    AssignTeamParticipantUsersInput,
    CreateTeamInput,
    PickableTeamParticipantUserFilterInput,
    TeamFilterInput,
    UpdateTeamInformationInput,
    UpdateTeamParticipantInput,
} from './inputs';
import { TeamRef } from './refs';
import { PickableTeamParticipantUserResource, TeamResource } from './resources';
import { OffsetPaginationInput } from '@app/data/inputs';
import qs from 'qs';

export default class Team {
    private basePath = '/api/team';
    private http: AxiosInstance;

    constructor(http: AxiosInstance) {
        this.http = http;
    }

    // Fetches list of users which can be assigned to a team
    getPickableParticipantUsers = async (
        pagination: OffsetPaginationInput,
        filter: PickableTeamParticipantUserFilterInput
    ): Promise<CollectionResource<PickableTeamParticipantUserResource, {}>> => {
        const query = qs.stringify({ ...pagination, ...(filter || {}) });

        const { data } = await this.http.get(`${this.basePath}/picker/participant-user?${query}`);

        return data;
    };

    // Create new team
    create = async (input: CreateTeamInput): Promise<TeamRef> => {
        const { data } = await this.http.post(`${this.basePath}/`, input);

        return data;
    };

    // Get list of teams
    get = async (filter?: TeamFilterInput): Promise<CollectionResource<TeamResource, {}>> => {
        const query = qs.stringify({ ...(filter || {}) });

        const { data } = await this.http.get(`${this.basePath}/?${query}`);

        return data;
    };

    // Get single team
    getById = async (id: ID): Promise<TeamResource> => {
        const { data } = await this.http.get(`${this.basePath}/${id}`);

        return data;
    };

    // Update team info
    update = async (id: ID, input: UpdateTeamInformationInput): Promise<TeamRef> => {
        const { data } = await this.http.patch(`${this.basePath}/${id}`, input);

        return data;
    };

    // Archive team
    archive = async (id: ID): Promise<TeamRef> => {
        const { data } = await this.http.delete(`${this.basePath}/${id}`);

        return data;
    };

    // Restore team
    restore = async (id: ID): Promise<TeamRef> => {
        const { data } = await this.http.post(`${this.basePath}/${id}/restore`);

        return data;
    };

    // Assign participant users
    assignParticipantUsers = async (id: ID, input: AssignTeamParticipantUsersInput): Promise<TeamRef> => {
        const { data } = await this.http.post(`${this.basePath}/${id}/participation`, input);

        return data;
    };

    // Update participation info
    updateParticipation = async (id: ID, userId: ID, input: UpdateTeamParticipantInput): Promise<TeamRef> => {
        const { data } = await this.http.put(`${this.basePath}/${id}/participation/${userId}`, input);

        return data;
    };

    // Remove participation
    removeParticipation = async (id: ID, userId: ID): Promise<TeamRef> => {
        const { data } = await this.http.delete(`${this.basePath}/${id}/participation/${userId}`);

        return data;
    };
}
